import * as React from 'react';
import { createSyncObservable, pageRenderingDoneSignal, useObservable } from '@avensia/excite';

const SCROLL_THRESHOLD: number = 10;

type ScrollDirection = 'up' | 'down' | null;

export interface UseScrollDirectionWithQuickSearchOptions {
  threshold?: number;
  headerHeight?: number;
  quickSearchRef?: React.RefObject<{ focusInput: () => void } | null>;
}

export const [quickSearchShowObservable] = createSyncObservable<boolean>(true);

export function useShowQuickSearchOnScroll(options: UseScrollDirectionWithQuickSearchOptions = {}) {
  const { threshold = SCROLL_THRESHOLD, headerHeight = 0 } = options;

  const lastScrollYRef = React.useRef<number>(__BROWSER__ ? window.scrollY : 0);
  const scrollDirectionRef = React.useRef<ScrollDirection>(null);
  const showQuickSearch = useObservable(quickSearchShowObservable);

  React.useEffect(
    () =>
      pageRenderingDoneSignal.on(() => {
        quickSearchShowObservable.emit(true);
        scrollDirectionRef.current = null;
      }),
    [],
  );

  const handleScroll = React.useCallback(() => {
    const currentScrollY = window.scrollY;
    const scrollDifference = currentScrollY - lastScrollYRef.current;

    if (Math.abs(scrollDifference) > threshold && currentScrollY >= headerHeight) {
      const newDirection: ScrollDirection = scrollDifference > 0 ? 'down' : 'up';

      if (scrollDirectionRef.current !== newDirection) {
        scrollDirectionRef.current = newDirection;

        if (newDirection === 'down') {
          quickSearchShowObservable.emit(false);
        } else if (newDirection === 'up') {
          quickSearchShowObservable.emit(true);
        }
      }
    }

    lastScrollYRef.current = currentScrollY;
  }, [threshold, headerHeight]);

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return {
    showQuickSearch,
  };
}
